import { type ButtonHTMLAttributes, type ReactNode } from "react";
import cx from "classix";
import SpinnerIcon from "../../icons/SpinnerIcon";

/**
 * Mode: primary, secondary, normal, search
 */

interface BaseButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    children: ReactNode;
    className?: string;
    disabled?: boolean;
    loading?: boolean;
    smaller?: boolean;
    mode?: "action" | "secondary" | "action-message" | "action-secondary" | "delete" | "delete-requested" | "search";
    iconBefore?: ReactNode;
    iconAfter?: ReactNode;
    show?: boolean;
    // Allow `data-*` hooks (data-button-action, data-needbox-id, ...) used by the
    // global click handler in the panel bundle.
    [key: `data-${string}`]: unknown;
}

export default function BaseButton({
    children,
    className,
    disabled,
    loading,
    smaller = false,
    mode = "action",
    iconBefore,
    iconAfter,
    show = true,
    ...rest
}: BaseButtonProps) {
    if (show === false) {
        return null;
    }

    return (
        <button
            className={cx(
                smaller ? "px-0.5 py-1.5" : "px-1 py-3",
                "appearance-none outline-0 rounded-lg grow font-medium group",
                mode === "action" && "border-0 text-white bg-primary-00 hover:bg-accent-bright disabled:bg-tertiary-00 disabled:hover:bg-tertiary-00",
                mode === "secondary" && "border-0 text-white bg-secondary-00 hover:bg-secondary-02 disabled:bg-tertiary-00 disabled:hover:bg-tertiary-00",
                mode === "action-message" && "border-0 bg-white text-black",
                mode === "action-secondary" && "border-0 bg-tertiary-10 text-black",
                mode === "delete" && "bg-error-04 text-error-00 border-error-00 hover:bg-error-03",
                mode === "delete-requested" && "bg-error-00 text-white border-error-00 hover:bg-error-01 disabled:bg-error-03 disabled:border-error-02",
                mode === "search" && "bg-neutral-80 hover:bg-neutral-70",
                "flex flex-row gap-2 justify-center items-center",
                className
            )}
            disabled={disabled || loading}
            {...rest}
        >
            {Boolean(!loading) && iconBefore}
            <span className={cx(loading ? "translate-x-0" : "-translate-x-4")}>
                {children}
            </span>

            <span className={cx(
                "inline-block group-disabled:block size-4 fill-secondary-05",
                loading ? "opacity-100 translate-x-0" : "opacity-0 translate-x-2"
            )}>
                <SpinnerIcon />
            </span>
            {Boolean(!loading) && iconAfter}
        </button>
    );
}